import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import MainLayout from '../../components/layout/MainLayout';
import {
  ShieldCheck, Loader2, AlertTriangle, LogIn, UserX,
  Globe, Users, Activity, Eye
} from 'lucide-react';
import { auditApi } from '../../services/api';
import { toast } from 'sonner';

const fmt = (iso) => {
  if (!iso) return '—';
  try { return new Date(iso).toLocaleString('en-IN', { dateStyle: 'medium', timeStyle: 'short' }); }
  catch { return iso; }
};

const SecurityDashboard = () => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let active = true;
    const load = async () => {
      try {
        const d = await auditApi.getSecurityDashboard();
        if (active) setData(d);
      } catch (err) {
        toast.error(err.message || 'Failed to load security dashboard');
      } finally {
        if (active) setLoading(false);
      }
    };
    load();
    return () => { active = false; };
  }, []);

  if (loading) {
    return (
      <MainLayout title="Security Dashboard" subtitle="Platform security overview">
        <div className="flex items-center justify-center py-32" data-testid="security-dashboard-loading">
          <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
        </div>
      </MainLayout>
    );
  }

  const stats = [
    { label: 'Failed Logins (24h)', value: data?.security?.failed_logins_24h || 0, icon: AlertTriangle, color: 'red' },
    { label: 'Successful Logins (24h)', value: data?.security?.successful_logins_24h || 0, icon: LogIn, color: 'green' },
    { label: 'Unauthorized Attempts', value: data?.security?.unauthorized_total || 0, icon: UserX, color: 'orange' },
    { label: 'Active Sessions', value: data?.sessions?.active || 0, icon: Activity, color: 'blue' },
    { label: 'Total Visitors', value: data?.visitors?.total || 0, icon: Globe, color: 'cyan' },
    { label: 'Registered', value: data?.visitors?.registered || 0, icon: Users, color: 'purple' },
    { label: 'Anonymous', value: data?.visitors?.anonymous || 0, icon: Eye, color: 'gray' },
    { label: 'Total Page Views', value: data?.page_views || 0, icon: Eye, color: 'pink' },
  ];

  return (
    <MainLayout title="Security Dashboard" subtitle="Platform security overview">
      <div data-testid="security-dashboard">
        <motion.div
          initial={{ opacity: 0, y: -20 }} animate={{ opacity: 1, y: 0 }}
          className="mb-8 p-6 rounded-2xl relative overflow-hidden"
          style={{ background: 'linear-gradient(135deg, rgba(16,185,129,0.95) 0%, rgba(59,130,246,0.95) 100%)' }}
        >
          <div className="relative z-10 flex items-center gap-4">
            <div className="p-4 rounded-2xl bg-white/20 backdrop-blur-sm">
              <ShieldCheck className="w-10 h-10 text-white" />
            </div>
            <div>
              <h2 className="text-2xl font-bold text-white">Security Monitoring</h2>
              <p className="text-white/80">Server-authoritative audit, visitor & session intelligence</p>
            </div>
          </div>
        </motion.div>

        <div className="grid grid-cols-4 gap-6 mb-8">
          {stats.map((s, i) => (
            <motion.div
              key={s.label}
              data-testid={`secdash-${s.label.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}
              initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.06 }}
              className="glass-card p-6 relative overflow-hidden"
            >
              <div className={`absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-${s.color}-500 to-${s.color}-400`} />
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-500">{s.label}</p>
                  <h3 className="text-3xl font-bold mt-1">{Number(s.value).toLocaleString()}</h3>
                </div>
                <div className={`p-3 rounded-xl bg-${s.color}-500/20`}>
                  <s.icon className={`w-6 h-6 text-${s.color}-500`} />
                </div>
              </div>
            </motion.div>
          ))}
        </div>

        <div className="grid grid-cols-2 gap-6">
          <div className="glass-card p-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2"><AlertTriangle className="w-5 h-5 text-red-500" />Recent Failed Logins</h3>
            <div className="space-y-2" data-testid="secdash-recent-failed">
              {(data?.recent_failed_logins || []).length === 0 ? (
                <p className="text-sm text-gray-500">No failed logins recorded.</p>
              ) : data.recent_failed_logins.map((e) => (
                <div key={e.id} className="p-3 rounded-lg bg-red-500/5 flex items-center justify-between text-sm">
                  <div>
                    <p>{e.actor_email || 'unknown'}</p>
                    <p className="text-xs text-gray-500 font-mono">{e.ip_address || '—'} · {e.details?.reason || ''}</p>
                  </div>
                  <span className="text-xs text-gray-500 whitespace-nowrap">{fmt(e.timestamp)}</span>
                </div>
              ))}
            </div>
          </div>

          <div className="glass-card p-6">
            <h3 className="font-semibold mb-4 flex items-center gap-2"><Globe className="w-5 h-5 text-orange-500" />Top IPs by Failed Logins</h3>
            <div className="space-y-2" data-testid="secdash-top-ips">
              {(data?.top_failed_ips || []).length === 0 ? (
                <p className="text-sm text-gray-500">No data.</p>
              ) : data.top_failed_ips.map((t) => (
                <div key={t.ip_address} className="p-3 rounded-lg bg-gray-500/5 flex items-center justify-between text-sm">
                  <span className="font-mono">{t.ip_address}</span>
                  <span className="px-2 py-0.5 rounded-full bg-red-500/20 text-red-500 text-xs">{t.count} attempts</span>
                </div>
              ))}
            </div>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export default SecurityDashboard;
